import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  AppWindow,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Hash,
  Loader2,
  Package,
  RefreshCw,
  RotateCw,
  Smartphone,
  Tag,
} from 'lucide-react';
import clsx from 'clsx';
import { adbApi, controlApi } from '@/lib/api';
import { tsLocal } from '@/lib/format';
import type {
  CommandSpec,
  ControlClientInfo,
  InstalledWshubApp,
} from '@/lib/types';
import { ControlCommandList } from '@/components/ControlCommandList';

/**
 * Mirror of the `app.icon` builtin reply — kept structurally compatible
 * with the Apps page so the React-Query cache (`['app.icon', serial,
 * pkg, versionCode]`) is shared between the two views. Only consulted
 * in the disconnected branch where there's no live `hello.app.name` to
 * draw from.
 */
interface AppIconResult {
  label?: string;
  labelZh?: string;
  iconPng?: string;
  versionName?: string;
  cached: boolean;
}

interface Props {
  /** Device serial — scopes the catalog + icon queries and the command
   *  list's per-(serial,pkg) pin / collapse local storage. */
  serial: string;
  /**
   * Stable package identity (drives the React key + all per-app caches).
   * Required because the panel can render BOTH connected and
   * disconnected states for the same pkg as it transitions — neither
   * `client.package` nor `installed.package` is guaranteed during the
   * brief window where session memory rehydrates a kill-then-reconnect.
   */
  pkg: string;
  /** Live session info when the app is currently connected via WS. */
  client?: ControlClientInfo;
  /**
   * Marker-scan result OR session-remembered fallback when the app is
   * NOT currently connected. Provides display name/icon/sdkVersion so
   * the panel stays in place after kill instead of flashing away.
   */
  installed?: InstalledWshubApp;
  /** True iff this app is currently the "active" pkg (drives run-card +
   *  events panel). Visual emphasis only — expansion is independent.
   *  Only meaningful when {@link client} is present. */
  isActive: boolean;
  /** Same semantic as the old ControlAppInfoCard dot: the device-side
   *  bridge is alive. Only meaningful when {@link client} is present. */
  connected: boolean;
  /** Currently expanded? Parent owns the expanded set so it survives
   *  across re-renders + multi-expand stays a global UI policy. */
  expanded: boolean;
  /** When `isActive`, the currently selected command name in the run
   *  card. Used to highlight the row in this panel's command list. */
  selectedCmdKey?: string;
  /** Toggle this panel's expanded state. */
  onToggleExpanded(): void;
  /** Pick a command in this panel: parent should setPkg(client.package)
   *  + setActive(cmdName) + ensure this panel is expanded. */
  onPickCmd(name: string): void;
  /** Inline ▷ in the command list: same as onPickCmd but also auto-runs
   *  zero-arg / non-subscribe commands (parent decides). */
  onRunCmd(spec: CommandSpec): void;
  /** Reconnect THIS app (connected branch only). */
  onReconnect(): void;
  /** Restart THIS app via chained `app.stop` → `app.start`. Called by
   *  the panel's own Restart button when disconnected. The parent
   *  should `refetch` both the clients + installedApps queries inside
   *  this handler so the row flips back to connected as soon as the
   *  hub sees a fresh hello. */
  onRestart(): void | Promise<void>;
  /** Disable reconnect / restart buttons while the parent's global WS
   *  dial is in flight — matches the old "重连中…" semantics. */
  busy: boolean;
}

/**
 * Unified per-app panel for the WS Commands page left rail.
 *
 * <p>Renders two visually-aligned states inside the SAME outer card so
 * the DOM node stays mounted across connected ↔ disconnected
 * transitions — no flash on kill, no position jump on reconnect:
 *
 * <ul>
 *   <li><b>connected</b>: full {@link ControlCommandList} body, green
 *       dot, 激活/重连 button. Active when {@code props.client} is
 *       defined and {@code props.connected} is true.</li>
 *   <li><b>disconnected</b>: minimal info body, rose dot, Restart
 *       button (chained {@code app.stop} → {@code app.start} via the
 *       parent-supplied {@code onRestart}). Active when {@code
 *       props.client} is undefined OR {@code props.connected} is
 *       false — sourced from {@code props.installed}.</li>
 * </ul>
 *
 * <p>The catalog query is gated on {@code expanded && isLive} so a
 * disconnected panel never burns bandwidth trying to fetch a catalog
 * over a dead WS, and the icon query is gated on {@code !isLive &&
 * !!installed.apkPath} so we don't double-pull the APK while the
 * connected branch already has {@code hello.app.name}.
 */
export function ControlClientPanel({
  serial,
  pkg,
  client,
  installed,
  isActive,
  connected,
  expanded,
  selectedCmdKey,
  onToggleExpanded,
  onPickCmd,
  onRunCmd,
  onReconnect,
  onRestart,
  busy,
}: Props): JSX.Element {
  const isLive = !!client && connected;

  // Catalog: connected branch only. The cache stays warm after
  // disconnect so a fast reconnect renders the command list instantly.
  const cat = useQuery({
    queryKey: ['control.catalog', serial, pkg],
    queryFn: () => controlApi.catalog(serial, pkg),
    enabled: expanded && isLive,
    staleTime: 30_000,
  });
  const commands = (cat.data?.commands ?? []) as CommandSpec[];

  // Icon: disconnected branch only. Shared cache key with the Apps
  // grid so we don't re-pull the APK when the user toggles between
  // the two pages.
  const iconQ = useQuery<AppIconResult>({
    queryKey: ['app.icon', serial, pkg, installed?.versionCode],
    queryFn: async () => {
      const out = await adbApi.run<AppIconResult>({
        serial,
        name: 'app.icon',
        args: {
          package: pkg,
          apkPath: installed?.apkPath,
          versionCode: installed?.versionCode,
        },
      });
      return out.result;
    },
    enabled: !isLive && !!installed?.apkPath,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  const uptime = useUptime(
    expanded && isLive && client ? client.connectedAt : undefined
  );

  // Restart bookkeeping — disconnected branch only. Local state means
  // the spinner / error live on this specific panel even if other
  // panels in the rail re-render.
  const [restartBusy, setRestartBusy] = useState(false);
  const [restartErr, setRestartErr] = useState<string | null>(null);

  // Click-to-copy bookkeeping for the package row. `pkgCopied` flips
  // true for ~1.2s after a successful copy so we can swap the trailing
  // `Copy` icon for a `Check` and surface a "已复制" tooltip — short
  // enough to feel snappy, long enough that the user actually sees it.
  const [pkgCopied, setPkgCopied] = useState(false);
  async function handleCopyPkg() {
    try {
      // `navigator.clipboard` requires a secure context (https / localhost).
      // Fall back to the legacy execCommand path for plain-http hub URLs
      // so the QA team running on `http://192.168.x.x:8990` still gets
      // copy support out of the box.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(pkg);
      } else {
        const ta = document.createElement('textarea');
        ta.value = pkg;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch {
      // Swallow — best-effort UX, copy failure is non-blocking and
      // typically surfaces via the missing "已复制" indicator.
    }
    setPkgCopied(true);
    window.setTimeout(() => setPkgCopied(false), 1200);
  }

  async function handleRestart() {
    setRestartErr(null);
    setRestartBusy(true);
    try {
      await onRestart();
    } catch (e) {
      setRestartErr((e as Error)?.message ?? '重启失败');
    } finally {
      setRestartBusy(false);
    }
  }

  /* ---------------------- Display data (state-aware) ---------------------- */
  // Prefer hello.app.* (most authoritative) when connected; fall back
  // to icon labelZh / label when we only have an installed snapshot.
  const displayName =
    client?.app.name ||
    iconQ.data?.labelZh ||
    iconQ.data?.label ||
    pkg;
  const displayVersion = client?.app.version || installed?.sdkVersion || '';

  const headerTitle = useMemo(() => {
    const lines: string[] = [];
    lines.push(`${displayName} ${displayVersion}`);
    if (client?.app.build) lines.push(`build: ${client.app.build}`);
    lines.push(`package: ${pkg}`);
    if (isLive && client) {
      lines.push(`connected: ${tsLocal(new Date(client.connectedAt).toISOString())}`);
      if (client.features.length > 0) {
        lines.push(`features: ${client.features.join(', ')}`);
      }
      if (client.capabilities.length > 0) {
        lines.push(`capabilities: ${client.capabilities.length}`);
      }
    } else {
      lines.push('状态: 未连接 — 点 Restart 拉起该 app');
      if (installed?.detection) {
        lines.push(
          `检测来源: ${installed.detection === 'marker' ? 'AndroidManifest marker' : '会话期 WS hello'}`
        );
      }
      if (installed?.lastSeenAt) {
        lines.push(`上次连接: ${tsLocal(new Date(installed.lastSeenAt).toISOString())}`);
      }
    }
    return lines.join('\n');
  }, [displayName, displayVersion, client, isLive, installed, pkg]);

  return (
    <div
      className={clsx(
        'card overflow-hidden transition',
        // Active outline only meaningful while connected — a stale
        // active highlight on a dead panel would mislead the user.
        isActive &&
          isLive &&
          'border-emerald-500 ring-2 ring-emerald-500/70 shadow-sm shadow-emerald-500/10'
      )}
    >
      {/* Header row — clickable to toggle expansion. */}
      <button
        type="button"
        className={clsx(
          'flex w-full min-w-0 items-center gap-2 px-2 py-2 text-left transition',
          'hover:bg-bg-soft'
        )}
        onClick={onToggleExpanded}
        title={headerTitle}
        aria-expanded={expanded}
      >
        {/* Connection dot — green/rose stays canonical across states. */}
        <span
          className={clsx(
            'inline-flex h-2 w-2 shrink-0 rounded-full',
            isLive ? 'bg-emerald-500' : 'bg-rose-500'
          )}
          title={isLive ? '设备适配器已连接' : '设备适配器已离线'}
        />

        {/* Icon thumbnail — disconnected branch only. Connected mode
            uses a richer hello.app payload + the Smartphone glyph
            below, so we skip the icon to keep the row dense. */}
        {!isLive && (
          <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-line bg-bg-soft">
            {iconQ.data?.iconPng ? (
              <img
                src={`data:image/png;base64,${iconQ.data.iconPng}`}
                alt=""
                className="h-full w-full object-contain"
              />
            ) : iconQ.isFetching ? (
              <Loader2 className="h-3 w-3 animate-spin text-ink-dim" />
            ) : (
              <AppWindow className="h-3.5 w-3.5 text-ink-dim" />
            )}
          </div>
        )}

        {/* Identity column — name + version on row 1, package on row 2.
            `items-start` (not baseline) so the Smartphone glyph + the
            version badge both anchor to the FIRST line of the name when
            it wraps to 2 lines — a baseline-aligned icon next to a
            two-line label looks broken because the baseline becomes the
            bottom row. */}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start gap-1.5">
            <Smartphone
              className={clsx(
                'mt-[3px] h-3.5 w-3.5 shrink-0',
                isLive ? 'text-accent' : 'text-ink-dim'
              )}
            />
            {/* App name wraps to as many lines as it needs — never
                truncated, never ellipsised. `break-words` is the only
                guardrail (long ASCII tokens like a UUID get mid-word
                break instead of overflowing horizontally); CJK
                already has soft break points between every char, and
                English wraps at spaces. The card grows vertically
                with the name — favoured tradeoff over hiding info. */}
            <span className="whitespace-normal break-words text-sm font-medium leading-tight text-ink">
              {displayName}
            </span>
            {/* Version badge — connected branch ONLY. In the
                disconnected branch the SDK / app version already
                appears in the expanded body (sdk / app rows), and
                showing it inline here was crowding out the app name
                in narrow sidebar widths. */}
            {isLive && displayVersion && (
              <span className="mt-[2px] shrink-0 font-mono text-[11px] text-ink-dim">
                {displayVersion}
              </span>
            )}
            {isActive && isLive && (
              <span
                className="ml-1 mt-[1px] shrink-0 rounded bg-emerald-500/15 px-1 text-[10px] font-medium text-emerald-500"
                title="当前 active：中栏运行卡与右栏 events 跟随这个 app"
              >
                active
              </span>
            )}
          </div>
          {/* Row 2: package + (disconnected) "未连" tag.
              `flex-wrap` so a long package name (com.long.scope.feature)
              wraps to a second/third line instead of truncating, and
              keeps the "未连" tag visible after the wrap. The pkg span
              is its own click target — copies the full package to
              clipboard, briefly swapping the trailing icon for ✓ to
              confirm. `stopPropagation` so clicking pkg doesn't also
              toggle the parent header's expand state. */}
          <div className="mt-0.5 flex min-w-0 flex-wrap items-start gap-x-1.5 gap-y-0.5 text-[11px] text-ink-dim">
            <Package className="mt-[2px] inline h-3 w-3 shrink-0" />
            <span
              role="button"
              tabIndex={0}
              aria-label={`复制包名 ${pkg}`}
              className={clsx(
                'inline-flex min-w-0 cursor-pointer items-start gap-1 break-words rounded px-0.5 transition',
                'hover:text-accent hover:bg-bg-soft',
                pkgCopied && 'text-emerald-500 hover:text-emerald-500'
              )}
              title={pkgCopied ? '已复制到剪贴板' : '点击复制包名'}
              onClick={(e) => {
                e.stopPropagation();
                void handleCopyPkg();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  void handleCopyPkg();
                }
              }}
            >
              <span className="break-words">{pkg}</span>
              {pkgCopied ? (
                <Check className="mt-[3px] h-3 w-3 shrink-0" />
              ) : (
                <Copy
                  className="mt-[3px] h-3 w-3 shrink-0 opacity-40 group-hover:opacity-100"
                  aria-hidden
                />
              )}
            </span>
            {!isLive && (
              <span
                className="shrink-0 rounded bg-rose-500/15 px-1 text-[9.5px] font-medium text-rose-500"
                title={
                  installed
                    ? installed.detection === 'marker'
                      ? '该 app 装了 SDK 但未通过 WS 连上来'
                      : '本会话中曾连上 hub，但现在没了'
                    : '未连接'
                }
              >
                未连
              </span>
            )}
          </div>
        </div>

        {/* Right-side action — Reconnect (live) / Restart (dead).
            Both buttons share the same height + width slot so the
            header layout doesn't shift across transitions. */}
        {isLive ? (
          <span
            role="button"
            tabIndex={0}
            aria-disabled={busy}
            className={clsx(
              'inline-flex h-7 shrink-0 items-center gap-1 rounded border px-2 text-[11px] transition',
              busy
                ? 'cursor-not-allowed border-line text-ink-dim'
                : isActive
                  ? 'border-emerald-500/60 text-emerald-500 hover:bg-emerald-500/10'
                  : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent'
            )}
            title={
              busy
                ? '重连中…'
                : isActive
                  ? '强制重连：清空 events、重订阅 events WS、刷新 catalog'
                  : '切到该 app + 刷新其 catalog（如已是 active 则同时重订阅 events WS）'
            }
            onClick={(e) => {
              e.stopPropagation();
              if (busy) return;
              onReconnect();
            }}
            onKeyDown={(e) => {
              if (busy) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onReconnect();
              }
            }}
          >
            <RefreshCw
              className={clsx('h-3 w-3', busy && isActive && 'animate-spin')}
            />
            {isActive ? '重连' : '激活'}
          </span>
        ) : (
          <span
            role="button"
            tabIndex={0}
            aria-disabled={restartBusy}
            className={clsx(
              'inline-flex h-7 shrink-0 items-center gap-1 rounded border px-2 text-[11px] transition',
              restartBusy
                ? 'cursor-not-allowed border-line text-ink-dim'
                : 'border-rose-500/40 text-rose-500 hover:bg-rose-500/10'
            )}
            title={
              restartBusy
                ? '重启中…'
                : 'app.stop → app.start：通过 adb 拉起该 app'
            }
            onClick={(e) => {
              e.stopPropagation();
              if (restartBusy) return;
              void handleRestart();
            }}
            onKeyDown={(e) => {
              if (restartBusy) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                void handleRestart();
              }
            }}
          >
            {restartBusy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCw className="h-3 w-3" />
            )}
            Restart
          </span>
        )}

        {/* Expand chevron */}
        <span className="shrink-0 text-ink-dim" aria-hidden>
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
      </button>

      {/* Body — only mounted when expanded. Branch by liveness; both
          branches share the same surrounding `border-t border-line p-2`
          so the visual transition is just the inner content swapping. */}
      {expanded && (
        <div className="space-y-2 border-t border-line p-2">
          {isLive && client ? (
            <>
              <dl className="grid grid-cols-[auto_1fr_auto_1fr] gap-x-2 gap-y-1 px-1 text-[10px] text-ink-muted">
                <dt className="inline-flex items-center gap-1 text-ink-dim">
                  <Tag className="h-3 w-3" /> ver
                </dt>
                <dd className="truncate font-mono text-ink" title={client.app.version}>
                  {client.app.version}
                </dd>
                <dt className="inline-flex items-center gap-1 text-ink-dim">
                  <Clock className="h-3 w-3" /> up
                </dt>
                <dd
                  className="text-ink"
                  title={tsLocal(new Date(client.connectedAt).toISOString())}
                >
                  {uptime}
                </dd>
                {client.app.build && (
                  <>
                    <dt className="inline-flex items-center gap-1 text-ink-dim">
                      <Hash className="h-3 w-3" /> build
                    </dt>
                    <dd
                      className="col-span-3 truncate font-mono text-ink"
                      title={client.app.build}
                    >
                      {client.app.build}
                    </dd>
                  </>
                )}
              </dl>

              {client.features.length > 0 && (
                <div className="flex flex-wrap gap-1 px-1">
                  {client.features.map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center rounded border border-line bg-bg-soft px-1.5 py-0.5 text-[10px] text-ink-muted"
                      title="设备适配器在 hello.features 声明的能力"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              )}

              <ControlCommandList
                commands={commands}
                activeName={isActive ? selectedCmdKey : undefined}
                onSelect={onPickCmd}
                onRun={onRunCmd}
                serial={serial}
                package={pkg}
                loading={cat.isLoading}
                error={cat.error as Error | undefined}
              />
            </>
          ) : (
            <>
              {/* Disconnected meta — sdk source / app version / last seen. */}
              <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 px-1 text-[10px] text-ink-muted">
                {displayVersion && (
                  <>
                    <dt className="inline-flex items-center gap-1 text-ink-dim">
                      <Tag className="h-3 w-3" /> sdk
                    </dt>
                    <dd className="truncate font-mono text-ink" title={displayVersion}>
                      {displayVersion}
                      {installed && (
                        <span className="ml-1 text-[9px] text-ink-dim">
                          ({installed.detection === 'marker' ? 'manifest' : 'hello'})
                        </span>
                      )}
                    </dd>
                  </>
                )}
                {iconQ.data?.versionName && (
                  <>
                    <dt className="inline-flex items-center gap-1 text-ink-dim">
                      <Tag className="h-3 w-3" /> app
                    </dt>
                    <dd className="truncate font-mono text-ink">
                      v{iconQ.data.versionName}
                      {installed?.versionCode && ` (${installed.versionCode})`}
                    </dd>
                  </>
                )}
                {installed?.lastSeenAt && (
                  <>
                    <dt className="inline-flex items-center gap-1 text-ink-dim">
                      上次连接
                    </dt>
                    <dd className="truncate text-ink">
                      {tsLocal(new Date(installed.lastSeenAt).toISOString())}
                    </dd>
                  </>
                )}
              </dl>

              <div className="rounded border border-rose-500/20 bg-rose-500/5 px-2 py-1.5 text-[11px] text-ink-muted">
                该 app 当前未通过 WS 连上 hub —— 命令目录不可用。点上方
                Restart 重新拉起该 app，连上之后该卡片会自动翻绿并展示完整命令列表。
              </div>

              {restartErr && (
                <div className="flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-400">
                  <AlertTriangle className="h-3 w-3" />
                  <span className="truncate" title={restartErr}>
                    {restartErr}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Ticks once per second so the connected-for label stays current.
 *  Pass `undefined` to disable the timer (collapsed panels / offline). */
function useUptime(connectedAt: number | undefined): string {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!connectedAt) return;
    const t = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(t);
  }, [connectedAt]);
  return useMemo(() => {
    if (!connectedAt) return '—';
    const sec = Math.max(0, Math.floor((now - connectedAt) / 1000));
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m}m ${sec % 60}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }, [connectedAt, now]);
}
